import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Video,
  MessageSquarePlus,
  Loader2,
  AlertCircle,
  RefreshCw,
  User,
  BarChart3,
  ChevronRight,
} from 'lucide-react';

import { DataState } from '../../context/DataProvider';
import { ProcessedContent } from '../../components/meeting/ProcessedMeetingContent';
import {
  getMeetingDetails,
  getUserConfiguredMeetingDetails,
} from '../../services/workflowApi';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const parseDate = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

const fmtTime = (d) =>
  d
    ? d.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '—';

const fmtDate = (d) =>
  d
    ? d.toLocaleDateString('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—';

const duration = (start, end) => {
  const s = parseDate(start?.dateTime);
  const e = parseDate(end?.dateTime);
  if (!s || !e) return null;
  const mins = Math.round((e - s) / 60000);
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
};

// ─── Attendee avatar ───────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-violet-600',
  'bg-blue-600',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-cyan-600',
  'bg-pink-600',
  'bg-teal-600',
];

const AttendeeAvatar = ({ name, email, index, size = 'sm' }) => {
  const initials = name
    ? name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (email?.[0] || '?').toUpperCase();
  const sizeClass = size === 'lg' ? 'h-9 w-9 text-sm' : 'h-7 w-7 text-[11px]';
  return (
    <div
      title={name || email}
      className={`group relative flex shrink-0 items-center justify-center rounded-full font-bold text-white ring-2 ring-[#0a0a0c] ${sizeClass} ${AVATAR_COLORS[index % AVATAR_COLORS.length]}`}
    >
      {initials}
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-zinc-800 px-2 py-1 text-[10px] text-zinc-200 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {name || email}
      </span>
    </div>
  );
};

// ─── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CFG = {
  processed: {
    label: 'Processed',
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-400',
    dot: 'bg-emerald-400',
  },
  processing: {
    label: 'Processing',
    bg: 'bg-blue-500/15',
    text: 'text-blue-400',
    dot: 'bg-blue-400 animate-pulse',
  },
  reprocessing: {
    label: 'Reprocessing',
    bg: 'bg-amber-500/15',
    text: 'text-amber-400',
    dot: 'bg-amber-400 animate-pulse',
  },
  failed: {
    label: 'Failed',
    bg: 'bg-red-500/15',
    text: 'text-red-400',
    dot: 'bg-red-400',
  },
  no_transcript: {
    label: 'No Transcript',
    bg: 'bg-zinc-500/15',
    text: 'text-zinc-400',
    dot: 'bg-zinc-400',
  },
  idle: {
    label: 'Pending',
    bg: 'bg-zinc-500/15',
    text: 'text-zinc-400',
    dot: 'bg-zinc-400',
  },
};

const StatusBadge = ({ status, size = 'md' }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.idle;
  const textSize = size === 'lg' ? 'text-xs' : 'text-[11px]';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold ${textSize} ${cfg.bg} ${cfg.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ─── Meta pill ─────────────────────────────────────────────────────────────────

const MetaPill = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1.5 text-sm text-zinc-400">
    <Icon className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
    {children}
  </div>
);

// ─── Main page ─────────────────────────────────────────────────────────────────

const MeetingSummaryPage = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isManual = searchParams.get('manual') === 'true';
  const { userGoogleId: accessToken } = DataState();

  const [notification, setNotification] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [transcriptStatus, setTranscriptStatus] = useState('idle');
  const [pageStatus, setPageStatus] = useState('loading');
  const [error, setError] = useState(null);
  const tabsRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!eventId || !accessToken) return;
    setPageStatus('loading');
    setError(null);
    try {
      const detail = isManual
        ? await getUserConfiguredMeetingDetails(accessToken, eventId)
        : await getMeetingDetails(accessToken, eventId);
      setNotification(detail);
      setProcessedData(detail.llm_summary || null);
      setTranscriptStatus(detail.transcript_status || 'idle');
      setPageStatus('idle');
    } catch (err) {
      setError(err?.message || 'Failed to load meeting details.');
      setPageStatus('failed');
    }
  }, [eventId, accessToken, isManual]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const subject = notification?.subject || 'Untitled Meeting';
  const organizer =
    notification?.organizer?.emailAddress || notification?.organizer || {};
  const attendees = notification?.attendees || [];
  const startDt = parseDate(notification?.start?.dateTime);
  const endDt = parseDate(notification?.end?.dateTime);
  const dur = duration(notification?.start, notification?.end);

  const meetingContext = { accessToken, eventId, isManual };

  const handleChatWithAgent = () => {
    const jiraIssues =
      processedData?.task_wise_summary?.map((t) => ({
        key: t.issue_key,
        summary: t.jira_details?.summary,
        status: t.jira_details?.status,
        assignee: t.jira_details?.assignee_name,
        priority: t.jira_details?.priority,
        is_blocked: t.is_blocked_in_meeting,
      })) || [];

    navigate('/chat', {
      state: {
        meetingContext: {
          eventId,
          meetingTitle: subject,
          isManual,
          llm_summary: processedData,
          jira_issues: jiraIssues,
          attendees: attendees.map((a) => {
            const ea = a?.emailAddress || a;
            return { name: ea?.name, email: ea?.address };
          }),
          action_items: processedData?.extracted_action_items || [],
          blockers: processedData?.unique_blockers || [],
        },
      },
    });
  };

  const handleOpenReport = () => {
    tabsRef.current?.scrollIntoView({ behavior: 'smooth' });
    window.dispatchEvent(
      new CustomEvent('meeting:open-tab', { detail: 'report' }),
    );
  };

  // ── Loading ──
  if (pageStatus === 'loading') {
    return (
      <div className="flex h-screen w-full flex-col bg-[#0a0a0c]">
        <BreadcrumbBar onBack={() => navigate(-1)} title="Loading…" />
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-[#5652CF]" />
            <p className="text-sm font-medium text-zinc-400">
              Loading meeting summary…
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (pageStatus === 'failed') {
    return (
      <div className="flex h-screen w-full flex-col bg-[#0a0a0c]">
        <BreadcrumbBar onBack={() => navigate(-1)} title="Error" />
        <div className="flex flex-1 items-center justify-center">
          <div className="flex max-w-md flex-col items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-8 text-center">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <h2 className="text-lg font-semibold text-zinc-100">
              Failed to Load
            </h2>
            <p className="text-sm text-zinc-400">{error}</p>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-700"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Go Back
              </button>
              <button
                onClick={fetchData}
                className="flex items-center gap-2 rounded-lg bg-[#5652CF] px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-[#4845b5]"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen w-full flex-col bg-[#0a0a0c]"
      style={{ scrollbarWidth: 'thin', scrollbarColor: '#3f3f46 transparent' }}
    >
      {/* ── Breadcrumb nav ─────────────────────────────────────────────── */}
      <BreadcrumbBar
        onBack={() => navigate(-1)}
        title={subject}
        onRefresh={fetchData}
      />

      {/* ── Hero section ───────────────────────────────────────────────── */}
      <div className="relative border-b border-zinc-800/60 bg-gradient-to-b from-zinc-900/70 via-zinc-900/30 to-transparent">
        {/* subtle grid background */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative mx-auto max-w-7xl px-4 pb-6 pt-5 sm:px-6 sm:pb-8 sm:pt-7 lg:px-8">
          {/* Row 1: icon + title + status */}
          <div className="mb-4 flex flex-wrap items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#5652CF]/20 ring-1 ring-[#5652CF]/30">
              <Video className="h-5 w-5 text-[#5652CF]" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight text-zinc-50">
                  {subject}
                </h1>
                <StatusBadge status={transcriptStatus} size="lg" />
              </div>
              {/* Row 2: meta pills */}
              <div className="mt-2 flex flex-wrap items-center gap-4">
                {startDt && (
                  <MetaPill icon={Calendar}>{fmtDate(startDt)}</MetaPill>
                )}
                {startDt && endDt && (
                  <MetaPill icon={Clock}>
                    {fmtTime(startDt)} – {fmtTime(endDt)}
                    {dur && (
                      <span className="ml-1.5 rounded bg-zinc-800 px-1.5 py-0.5 text-[11px] text-zinc-400">
                        {dur}
                      </span>
                    )}
                  </MetaPill>
                )}
                {(organizer.name || organizer.address) && (
                  <MetaPill icon={User}>
                    <span className="text-zinc-500">Organized by</span>
                    <span className="ml-1 font-medium text-zinc-300">
                      {organizer.name || organizer.address}
                    </span>
                  </MetaPill>
                )}
              </div>
            </div>

            {/* Action buttons — top-right of hero */}
            <div className="flex w-full shrink-0 flex-wrap items-center gap-2.5 sm:w-auto">
              {processedData && (
                <button
                  onClick={handleOpenReport}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-400 transition-all hover:bg-emerald-500/20 active:scale-95 sm:flex-none"
                >
                  <BarChart3 className="h-4 w-4" />
                  Sprint Report
                </button>
              )}
              <button
                onClick={handleChatWithAgent}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#5652CF] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#5652CF]/25 transition-all hover:bg-[#4845b5] active:scale-95 sm:flex-none"
              >
                <MessageSquarePlus className="h-4 w-4" />
                Chat with Agent
              </button>
            </div>
          </div>

          {/* Row 3: attendees */}
          {attendees.length > 0 && (
            <div className="mt-2 flex items-center gap-3">
              <div className="flex -space-x-2">
                {attendees.slice(0, 8).map((a, i) => {
                  const ea = a?.emailAddress || a;
                  return (
                    <AttendeeAvatar
                      key={ea?.address || i}
                      name={ea?.name}
                      email={ea?.address}
                      index={i}
                    />
                  );
                })}
                {attendees.length > 8 && (
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-[11px] font-bold text-zinc-300 ring-2 ring-[#0a0a0c]">
                    +{attendees.length - 8}
                  </div>
                )}
              </div>
              <span className="text-xs text-zinc-500">
                {attendees.length} attendee{attendees.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Row 4: description preview */}
          {notification?.bodyPreview && (
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-500">
              {notification.bodyPreview}
            </p>
          )}
        </div>
      </div>

      {/* ── Tab content area ───────────────────────────────────────────── */}
      <div
        ref={tabsRef}
        className="mx-auto w-full max-w-7xl flex-1 px-4 py-5 sm:px-6 sm:py-7 lg:px-8"
      >
        {processedData ? (
          <ProcessedContent
            data={processedData}
            meetingContext={meetingContext}
          />
        ) : transcriptStatus === 'processing' ||
          transcriptStatus === 'reprocessing' ? (
          <div className="flex flex-col items-center justify-center py-28 text-center">
            <Loader2 className="mb-4 h-9 w-9 animate-spin text-[#5652CF]" />
            <p className="text-base font-semibold text-zinc-300">
              Processing Meeting Transcript
            </p>
            <p className="mt-1.5 text-sm text-zinc-500">
              This may take a few minutes. Refresh to check for updates.
            </p>
            <button
              onClick={fetchData}
              className="mt-5 flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-800/60 px-4 py-2 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-700"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-28 text-center">
            <AlertCircle className="mb-4 h-9 w-9 text-zinc-600" />
            <p className="text-base font-semibold text-zinc-400">
              No Processed Data Available
            </p>
            <p className="mt-1.5 text-sm text-zinc-600">
              {transcriptStatus === 'failed'
                ? 'Transcript processing failed. Try reprocessing.'
                : 'The transcript has not been processed yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Breadcrumb bar ────────────────────────────────────────────────────────────

const BreadcrumbBar = ({ onBack, title, onRefresh }) => (
  <div className="flex shrink-0 items-center justify-between gap-3 border-b border-zinc-800/70 bg-[#0a0a0c] px-4 py-2.5 sm:px-6">
    <div className="flex items-center gap-2 text-xs text-zinc-500">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Meetings
      </button>
      <ChevronRight className="h-3 w-3 text-zinc-700" />
      <span className="max-w-xs truncate font-medium text-zinc-400">
        {title}
      </span>
    </div>
    {onRefresh && (
      <button
        onClick={onRefresh}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
        title="Refresh"
      >
        <RefreshCw className="h-3 w-3" />
        Refresh
      </button>
    )}
  </div>
);

export default MeetingSummaryPage;
